import * as THREE from 'three';

const HOSTILE_DEATH_DAMAGE_TYPES = new Set([
  'mob',
  'zombie',
  'skeleton',
  'skeleton_archer',
  'creeper',
  'spider',
  'enderman',
  'husk',
  'stray',
  'pillager',
  'raider_brute',
  'drowned',
  'ender_dragon'
]);

const DEATH_REASON_VARIANT_COUNT = {
  default: 3,
  mob: 4,
  fall: 3,
  void: 3,
  starve: 3,
  drown: 3,
  lava: 3,
  cactus: 3,
  bed_explode: 3,
  creeper_explode: 3
};

const DEATH_MOB_NAME_FALLBACK = {
  zombie: 'Zombie',
  skeleton: 'Skeleton',
  skeleton_archer: 'Skeleton Archer',
  creeper: 'Creeper',
  spider: 'Spider',
  enderman: 'Enderman',
  husk: 'Husk',
  stray: 'Stray',
  pillager: 'Pillager',
  raider_brute: 'Raider Brute',
  drowned: 'Drowned',
  ender_dragon: 'Ender Dragon'
};

export function createDeathFeedbackSystem({
  camera,
  controls,
  t,
  hurtOverlayEl = null,
  damageDirectionIndicatorEl = null,
  deathOverlayEl = null,
  deathTextEl = null,
  deathReasonEl = null,
  deathAnimationSeconds = 3.0,
  onBeforeDeathAnimationStart = null,
  onDeathAnimationComplete = null
} = {}) {
  const damageSourceDir = new THREE.Vector3();
  const damageForwardDir = new THREE.Vector3();
  const cameraRollFixEuler = new THREE.Euler(0, 0, 0, 'YXZ');

  let hurtFlashIntensity = 0;
  let damageIndicatorIntensity = 0;
  let damageIndicatorAngleDeg = 0;

  let playerDeathAnimating = false;
  let deathAnimationTimer = 0;
  let deathCameraStartY = 0;
  let lastDeathDamageType = 'mob';
  let lastDeathReasonVariant = 1;

  function clamp01(value) {
    return THREE.MathUtils.clamp(value, 0, 1);
  }

  function easeOutCubic(value) {
    const normalized = clamp01(value);
    return 1 - ((1 - normalized) * (1 - normalized) * (1 - normalized));
  }

  function getDeathReasonGroup(type = 'mob') {
    const normalized = String(type || 'mob');
    if (Object.prototype.hasOwnProperty.call(DEATH_REASON_VARIANT_COUNT, normalized)) return normalized;
    if (HOSTILE_DEATH_DAMAGE_TYPES.has(normalized)) return 'mob';
    return 'default';
  }

  function rollDeathReasonVariant(group = 'default') {
    const count = Math.max(1, Math.floor(DEATH_REASON_VARIANT_COUNT[group] || 1));
    return 1 + Math.floor(Math.random() * count);
  }

  function getLocalizedMobDeathName(type = 'mob') {
    const normalized = String(type || 'mob');
    const key = `mob.${normalized}`;
    const localized = t(key);
    if (localized !== key) return localized;
    return DEATH_MOB_NAME_FALLBACK[normalized] || normalized;
  }

  function getDeathReasonText(type = lastDeathDamageType, variant = lastDeathReasonVariant) {
    const normalized = String(type || 'mob');
    const group = getDeathReasonGroup(normalized);
    const maxVariant = Math.max(1, Math.floor(DEATH_REASON_VARIANT_COUNT[group] || 1));
    const safeVariant = THREE.MathUtils.clamp(Math.floor(Number(variant) || 1), 1, maxVariant);
    const key = `death.reason.${group}.${safeVariant}`;
    if (group === 'mob') {
      return t(key, { mob: getLocalizedMobDeathName(normalized) });
    }
    return t(key);
  }

  function renderDeathOverlayText() {
    if (deathTextEl) deathTextEl.innerText = t('death.title');
    if (deathReasonEl) deathReasonEl.innerText = getDeathReasonText(lastDeathDamageType, lastDeathReasonVariant);
  }

  function setDamageIndicatorBySource(sourcePosition) {
    if (!sourcePosition || !camera || !controls) return false;
    if (!Number.isFinite(sourcePosition.x) || !Number.isFinite(sourcePosition.y) || !Number.isFinite(sourcePosition.z)) return false;
    const playerPos = controls.getObject().position;

    damageSourceDir.set(
      sourcePosition.x - playerPos.x,
      0,
      sourcePosition.z - playerPos.z
    );
    if (damageSourceDir.lengthSq() < 0.0001) return false;
    damageSourceDir.normalize();

    camera.getWorldDirection(damageForwardDir);
    damageForwardDir.y = 0;
    if (damageForwardDir.lengthSq() < 0.0001) {
      damageForwardDir.set(0, 0, -1);
    } else {
      damageForwardDir.normalize();
    }

    const dot = THREE.MathUtils.clamp(damageForwardDir.dot(damageSourceDir), -1, 1);
    const crossY = (damageForwardDir.z * damageSourceDir.x) - (damageForwardDir.x * damageSourceDir.z);
    damageIndicatorAngleDeg = THREE.MathUtils.radToDeg(Math.atan2(crossY, dot));
    return true;
  }

  function triggerDamageFeedback(type = 'mob', sourcePosition = null, damage = 1) {
    const normalizedDamage = THREE.MathUtils.clamp((Number.isFinite(damage) ? damage : 1) / 8, 0.2, 1);
    const extra = type === 'void' ? 0.38 : 0;
    // 更強烈的紅色閃爍 — 基礎值提高，傷害縮放更大
    hurtFlashIntensity = Math.min(1, hurtFlashIntensity + 0.35 + normalizedDamage * 0.55 + extra);

    if (setDamageIndicatorBySource(sourcePosition)) {
      // 方向指示器更明顯
      damageIndicatorIntensity = Math.min(1, damageIndicatorIntensity + 0.5 + normalizedDamage * 0.5);
    }
  }

  function updateDamageFeedbackOverlay(delta = 0) {
    // 衰減更慢 — 紅色持續更久，打擊感更強
    hurtFlashIntensity = Math.max(0, hurtFlashIntensity - Math.max(0, delta) * 1.4);
    damageIndicatorIntensity = Math.max(0, damageIndicatorIntensity - Math.max(0, delta) * 1.6);

    if (hurtOverlayEl) {
      const opacity = clamp01(hurtFlashIntensity);
      hurtOverlayEl.style.opacity = `${opacity.toFixed(3)}`;
    }

    if (damageDirectionIndicatorEl) {
      const opacity = clamp01(damageIndicatorIntensity);
      const scale = 0.92 + opacity * 0.35;
      damageDirectionIndicatorEl.style.opacity = `${opacity.toFixed(3)}`;
      damageDirectionIndicatorEl.style.transform = `translate(-50%, -50%) rotate(${damageIndicatorAngleDeg.toFixed(2)}deg) scale(${scale.toFixed(3)})`;
    }
  }

  function resetDeathCameraRoll() {
    if (!camera) return;
    cameraRollFixEuler.setFromQuaternion(camera.quaternion, 'YXZ');
    if (!Number.isFinite(cameraRollFixEuler.x) || !Number.isFinite(cameraRollFixEuler.y)) {
      cameraRollFixEuler.set(0, 0, 0, 'YXZ');
    }
    cameraRollFixEuler.z = 0;
    camera.quaternion.setFromEuler(cameraRollFixEuler);
  }

  function startDeathAnimation(type = 'mob', sourcePosition = null) {
    if (playerDeathAnimating || !controls) return false;
    const normalizedType = String(type || 'mob');
    lastDeathDamageType = normalizedType;
    lastDeathReasonVariant = rollDeathReasonVariant(getDeathReasonGroup(normalizedType));
    renderDeathOverlayText();

    resetDeathCameraRoll();
    playerDeathAnimating = true;
    deathAnimationTimer = 0;
    deathCameraStartY = controls.getObject().position.y;

    triggerDamageFeedback(type, sourcePosition, 10);
    onBeforeDeathAnimationStart?.({
      type: normalizedType,
      sourcePosition
    });

    if (deathOverlayEl) deathOverlayEl.style.opacity = '0';
    return true;
  }

  function updateDeathAnimation(delta = 0) {
    if (!playerDeathAnimating || !controls || !camera) return false;

    deathAnimationTimer += Math.max(0, delta);
    const progress = clamp01(deathAnimationTimer / Math.max(0.0001, deathAnimationSeconds));
    const eased = easeOutCubic(progress);

    controls.getObject().position.y = deathCameraStartY - 0.64 * eased;
    camera.rotation.z = -0.58 * eased;

    if (deathOverlayEl) {
      deathOverlayEl.style.opacity = `${(0.25 + eased * 0.72).toFixed(3)}`;
    }

    if (deathAnimationTimer < deathAnimationSeconds) return true;

    playerDeathAnimating = false;
    deathAnimationTimer = 0;
    resetDeathCameraRoll();
    if (deathOverlayEl) deathOverlayEl.style.opacity = '0';
    onDeathAnimationComplete?.({
      type: lastDeathDamageType
    });
    return false;
  }

  function isDeathAnimating() {
    return playerDeathAnimating;
  }

  return {
    triggerDamageFeedback,
    updateDamageFeedbackOverlay,
    startDeathAnimation,
    updateDeathAnimation,
    resetDeathCameraRoll,
    refreshLocale: renderDeathOverlayText,
    isDeathAnimating
  };
}
